import type {
  AnswerValue,
  Choice,
  ConditionalDisplayCondition,
  Question,
  QuestionSettings,
} from '@/types/form'

type DependentChoicesByQuestionValue = Record<string, string[]>

function getDependentChoiceMap(question: Question): DependentChoicesByQuestionValue | null {
  const configured = question.settings.dependentChoicesByQuestionValue
  if (!configured || typeof configured !== 'object' || Array.isArray(configured)) return null

  const entries = Object.entries(configured).filter(
    (entry): entry is [string, string[]] =>
      Array.isArray(entry[1]) && entry[1].every((value) => typeof value === 'string'),
  )
  if (entries.length === 0) return null

  return Object.fromEntries(entries)
}

function normalizeAnswerKey(value: AnswerValue | undefined) {
  return typeof value === 'string' ? value.trim() : ''
}

function hasAnswer(value: AnswerValue | undefined) {
  return (
    value !== undefined &&
    value !== null &&
    !(typeof value === 'string' && value.trim() === '') &&
    !(Array.isArray(value) && value.length === 0)
  )
}

function hasConditionalDisplayAnswer(value: AnswerValue | undefined) {
  return hasAnswer(value)
}

function evaluateConditionalDisplayCondition(
  condition: ConditionalDisplayCondition,
  answers: Map<string, AnswerValue>,
) {
  const controllerValue = answers.has(condition.controllerQuestionId)
    ? answers.get(condition.controllerQuestionId)
    : condition.sourceControllerQuestionId
      ? answers.get(condition.sourceControllerQuestionId)
      : undefined
  const isAnswered = hasConditionalDisplayAnswer(controllerValue)

  if (condition.mode === 'show_when_answered') return isAnswered
  if (condition.mode === 'show_when_not_answered') return !isAnswered
  if (condition.mode === 'show_when_equals') {
    return String(controllerValue ?? '') === String(condition.controllerValue ?? '')
  }

  return true
}

function isConditionalDisplayCondition(value: unknown): value is ConditionalDisplayCondition {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    typeof (value as ConditionalDisplayCondition).controllerQuestionId === 'string'
  )
}

function getConditionalDisplayConditionGroups(
  display: QuestionSettings['conditionalDisplay'],
): ConditionalDisplayCondition[][] {
  if (!display) return []

  if (Array.isArray(display.conditionGroups)) {
    return display.conditionGroups
      .filter((group): group is ConditionalDisplayCondition[] => Array.isArray(group))
      .map((group) => group.filter(isConditionalDisplayCondition))
      .filter((group) => group.length > 0)
  }

  if (Array.isArray(display.conditions)) {
    const conditions = display.conditions.filter(isConditionalDisplayCondition)
    if (conditions.length === 0) return []
    return display.operator === 'OR' ? conditions.map((condition) => [condition]) : [conditions]
  }

  return isConditionalDisplayCondition(display) ? [[display]] : []
}

export function shouldShowQuestionByConditionalDisplay(
  question: Question,
  answers: Map<string, AnswerValue>,
) {
  const groups = getConditionalDisplayConditionGroups(question.settings.conditionalDisplay)
  if (groups.length === 0) return true

  return groups.some((group) =>
    group.every((condition) => evaluateConditionalDisplayCondition(condition, answers)),
  )
}

export function getDependentChoiceControllerQuestionId(question: Question) {
  const configured = question.settings.dependsOnQuestionId
  return typeof configured === 'string' && configured.trim() ? configured : null
}

export function getDependentChoiceEmptyMessage(question: Question) {
  const configured = question.settings.dependentChoiceEmptyMessage
  return typeof configured === 'string' && configured.trim()
    ? configured
    : '請先完成前一題，才會顯示可選項目。'
}

export function getFilteredDependentChoices(
  question: Question,
  answers?: Map<string, AnswerValue>,
): Choice[] {
  const controllerQuestionId = getDependentChoiceControllerQuestionId(question)
  const choiceMap = getDependentChoiceMap(question)
  if (!controllerQuestionId || !choiceMap) return question.choices

  const controllerValue = normalizeAnswerKey(answers?.get(controllerQuestionId))
  if (!controllerValue) return []

  const allowedValues = choiceMap[controllerValue]
  if (!allowedValues) return []

  const allowed = new Set(allowedValues)
  return question.choices.filter((choice) => allowed.has(choice.value) || allowed.has(choice.label))
}

export function clearInvalidDependentAnswers(
  questions: Question[],
  answers: Map<string, AnswerValue>,
): Map<string, AnswerValue> {
  const nextAnswers = new Map(answers)
  let changed = true

  while (changed) {
    changed = false
    for (const question of questions) {
      if (!getDependentChoiceControllerQuestionId(question)) continue
      if (!nextAnswers.has(question.id)) continue

      const currentAnswer = nextAnswers.get(question.id)
      const filteredChoices = getFilteredDependentChoices(question, nextAnswers)
      const isStillAllowed =
        typeof currentAnswer === 'string' &&
        filteredChoices.some(
          (choice) => choice.value === currentAnswer || choice.label === currentAnswer,
        )

      if (!isStillAllowed) {
        nextAnswers.delete(question.id)
        changed = true
      }
    }
  }

  return nextAnswers
}

export function clearInvalidConditionalAnswers(
  questions: Question[],
  answers: Map<string, AnswerValue>,
): Map<string, AnswerValue> {
  const nextAnswers = new Map(answers)
  let changed = true

  while (changed) {
    changed = false
    for (const question of questions) {
      if (!question.settings.conditionalDisplay) continue
      if (!nextAnswers.has(question.id)) continue
      if (!hasAnswer(nextAnswers.get(question.id))) continue
      if (shouldShowQuestionByConditionalDisplay(question, nextAnswers)) continue

      nextAnswers.delete(question.id)
      changed = true
    }
  }

  return nextAnswers
}
